import { ChangeEvent, memo, useMemo } from 'react'
import cn from 'classnames'
import { PinInputProps } from './pin-input.interface'
import './pin-input.style.scss'

const REGEX = new RegExp(/^(\d+|[A-Za-z]+)$/)

export const PinInputComponent = (props: PinInputProps) => {
  const {
    value,
    onChange,
    className,
    size = 'md',
    valueLength = 1,
    status = 'normal',
    variant = 'outline',
    ...restProps
  } = props

  const valueItems = useMemo(() => {
    const valueArray = value ? value.split('') : ''
    const items: Array<string> = []

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i]
      if (REGEX.test(char)) {
        items.push(char)
      } else {
        items.push('')
      }
    }

    return items
  }, [value, valueLength])
  
  const focusToNextInput = (target: HTMLElement) => {
    const nextElementSibling = target.nextElementSibling as HTMLInputElement | null

    if (nextElementSibling) {
      nextElementSibling.focus()
    }
  }

  const focusToPrevInput = (target: HTMLElement) => {
    const previousElementSibling = target.previousElementSibling as HTMLInputElement | null

    if (previousElementSibling) {
      previousElementSibling.focus()
    }
  }

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const  { target } = e
    const targetValue = target.value.trim()
    const targetValueLength = targetValue.length
    const isCharacterValid = REGEX.test(targetValue)

    if (!isCharacterValid && targetValue) {
      return
    }

    const nextInputEl = target.nextElementSibling as HTMLInputElement | null

    // only delete digit if next input element has no value
    if (!isCharacterValid && nextInputEl && nextInputEl.value !== '') {
      return
    }

    if (targetValueLength === 1) {
      const newValue = value ? value.substring(0, idx) + targetValue + value.substring(idx + 1) : targetValue
      onChange?.(newValue)

      if (!isCharacterValid) {
        return
      }

      focusToNextInput(target)
    } else if (targetValueLength === valueLength) {
      onChange?.(targetValue)

      target.blur()
    }
  }

  const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e
    const target = e.target as HTMLInputElement

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault()
      return focusToNextInput(target)
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault()
      return focusToPrevInput(target)
    }

    const targetValue = target.value

    // keep the selection range position, if the same digit was typed
    target.setSelectionRange(0, targetValue.length)

    if (e.key !== 'Backspace' || targetValue !== '') {
      return
    }

    focusToPrevInput(target)
  }

  const inputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { target } = e

    // keep focusing back until previous input element has value
    const prevInputEl = target.previousElementSibling as HTMLInputElement | null

    if (prevInputEl && prevInputEl.value === '') {
      return prevInputEl.focus()
    }

    target.setSelectionRange(0, target.value.length)
  }

  const classWrapper = cn ( 'legion-pin-input', className)

  const classInput = cn ({
    [`legion-pin-input__field`]: true,
    [`legion-pin-input__field--${status}`]: true,
    [`legion-pin-input__field--${size}`]: true,
    [`legion-pin-input__field--${variant}`]: true,
  })

  return (
    <div className={classWrapper}>
      {valueItems.map((digit, idx) => (
        <input
          {...restProps}
          key={idx}
          inputMode='numeric'
          defaultValue={digit}
          onFocus={inputOnFocus}
          className={classInput}
          maxLength={valueLength}
          onKeyDown={inputOnKeyDown}
          onChange={(e: ChangeEvent<HTMLInputElement>) => inputOnChange(e, idx)}
        />
      ))}
    </div>
  )
}

export const PinInput = memo(PinInputComponent)
